import { z } from "zod";

export const NODE_STAGES = [
  "MAPPED",
  "QOZ_VERIFIED",
  "COMMUNITY_AUTHORIZED",
  "SITE_CONTROLLED",
  "INTERCONNECTION_SCREENED",
  "CONTRACT_QUALIFIED",
  "FINANCED",
  "CONSTRUCTED",
  "ENERGIZED",
  "MEASURED",
] as const;

export const ORG_INTEGRATION_STATES = [
  "INTAKE",
  "VERIFICATION",
  "SCOPE_REVIEW",
  "PENDING_SETC_APPROVAL",
  "ACTIVE",
  "SUSPENDED",
  "OFFBOARDED",
] as const;

export const ORG_RELATIONSHIP_TYPES = [
  "INTERNAL",
  "AFFILIATE",
  "EXTERNAL_PARTNER",
  "VENDOR",
  "REGULATOR",
  "OTHER",
] as const;

export const ORG_CATEGORY_CODES = [
  "EDUCATION",
  "FINANCIAL_SERVICES",
  "HEALTHCARE",
  "HEALTH_INSURANCE",
  "TOURISM_HOSPITALITY",
  "MANUFACTURING",
  "ENERGY_INFRASTRUCTURE",
  "GOVERNMENT_REGULATION",
  "COMMUNITY_NONPROFIT",
  "PROFESSIONAL_SERVICES",
] as const;

export const ORG_SUBCATEGORY_CODES = [
  "HBCU",
  "POST_SECONDARY_INSTITUTION",
  "COMMUNITY_COLLEGE",
  "TECHNICAL_VOCATIONAL",
  "TRAINING_PROVIDER",
  "CDFI",
  "FINANCIAL_INSTITUTION",
  "COMMERCIAL_BANK",
  "CREDIT_UNION",
  "BUILDING_SOCIETY",
  "LOAN_FUND",
  "MICROFINANCE_INSTITUTION",
  "PREVENTIVE_HEALTH_PROVIDER",
  "PRIMARY_CARE",
  "DIAGNOSTIC_SERVICE",
  "PHARMACY",
  "WELLNESS_PROVIDER",
  "HOSPITAL",
  "HEALTH_INSURANCE_ISSUER",
  "MANAGED_CARE_ORGANIZATION",
  "PUBLIC_HEALTH_PLAN",
  "EMPLOYER_HEALTH_PLAN",
  "HOTEL",
  "RESORT",
  "GUESTHOUSE",
  "VILLA",
  "ATTRACTION",
  "TOURISM_ASSOCIATION",
  "MANUFACTURING_COMPANY",
  "FOOD_BEVERAGE_MANUFACTURER",
  "PHARMACEUTICAL_MANUFACTURER",
  "TEXTILE_MANUFACTURER",
  "CONSTRUCTION_MATERIALS_MANUFACTURER",
  "ELECTRONICS_MANUFACTURER",
  "INDUSTRIAL_EQUIPMENT_MANUFACTURER",
  "UTILITY",
  "RENEWABLE_DEVELOPER",
  "EPC_CONTRACTOR",
  "EQUIPMENT_SUPPLIER",
  "GRID_OPERATOR",
  "MINISTRY",
  "REGULATOR",
  "MUNICIPALITY",
  "TOURISM_AUTHORITY",
  "DEVELOPMENT_AGENCY",
  "NONPROFIT",
  "FAITH_BASED_ORGANIZATION",
  "COMMUNITY_ORGANIZATION",
  "FOUNDATION",
  "LEGAL_SERVICES",
  "ACCOUNTING_SERVICES",
  "ENGINEERING_SERVICES",
  "TECHNOLOGY_SERVICES",
  "CONSULTING_SERVICES",
] as const;

export const ORGANIZATION_TAXONOMY = [
  {
    code: "EDUCATION",
    name: "Education",
    subcategories: [
      ["HBCU", "Historically Black College or University"],
      ["POST_SECONDARY_INSTITUTION", "Post-secondary institution"],
      ["COMMUNITY_COLLEGE", "Community college"],
      ["TECHNICAL_VOCATIONAL", "Technical or vocational institution"],
      ["TRAINING_PROVIDER", "Training provider"],
    ],
  },
  {
    code: "FINANCIAL_SERVICES",
    name: "Financial services",
    subcategories: [
      ["CDFI", "Community Development Financial Institution"],
      ["FINANCIAL_INSTITUTION", "Financial institution"],
      ["COMMERCIAL_BANK", "Commercial bank"],
      ["CREDIT_UNION", "Credit union"],
      ["BUILDING_SOCIETY", "Building society"],
      ["LOAN_FUND", "Loan fund"],
      ["MICROFINANCE_INSTITUTION", "Microfinance institution"],
    ],
  },
  {
    code: "HEALTHCARE",
    name: "Healthcare",
    subcategories: [
      ["PREVENTIVE_HEALTH_PROVIDER", "Preventive-health provider"],
      ["PRIMARY_CARE", "Primary care"],
      ["DIAGNOSTIC_SERVICE", "Diagnostic service"],
      ["PHARMACY", "Pharmacy"],
      ["WELLNESS_PROVIDER", "Wellness provider"],
      ["HOSPITAL", "Hospital"],
    ],
  },
  {
    code: "HEALTH_INSURANCE",
    name: "Health insurance",
    subcategories: [
      ["HEALTH_INSURANCE_ISSUER", "Health-insurance issuer"],
      ["MANAGED_CARE_ORGANIZATION", "Managed-care organization"],
      ["PUBLIC_HEALTH_PLAN", "Public health plan"],
      ["EMPLOYER_HEALTH_PLAN", "Employer health plan"],
    ],
  },
  {
    code: "TOURISM_HOSPITALITY",
    name: "Tourism and hospitality",
    subcategories: [
      ["HOTEL", "Hotel"],
      ["RESORT", "Resort"],
      ["GUESTHOUSE", "Guesthouse"],
      ["VILLA", "Villa"],
      ["ATTRACTION", "Visitor attraction"],
      ["TOURISM_ASSOCIATION", "Tourism association"],
    ],
  },
  {
    code: "MANUFACTURING",
    name: "Manufacturing",
    subcategories: [
      ["MANUFACTURING_COMPANY", "Manufacturing company"],
      ["FOOD_BEVERAGE_MANUFACTURER", "Food and beverage manufacturer"],
      ["PHARMACEUTICAL_MANUFACTURER", "Pharmaceutical manufacturer"],
      ["TEXTILE_MANUFACTURER", "Textile manufacturer"],
      [
        "CONSTRUCTION_MATERIALS_MANUFACTURER",
        "Construction-materials manufacturer",
      ],
      ["ELECTRONICS_MANUFACTURER", "Electronics manufacturer"],
      [
        "INDUSTRIAL_EQUIPMENT_MANUFACTURER",
        "Industrial-equipment manufacturer",
      ],
    ],
  },
  {
    code: "ENERGY_INFRASTRUCTURE",
    name: "Energy and infrastructure",
    subcategories: [
      ["UTILITY", "Utility"],
      ["RENEWABLE_DEVELOPER", "Renewable-energy developer"],
      ["EPC_CONTRACTOR", "EPC contractor"],
      ["EQUIPMENT_SUPPLIER", "Equipment supplier"],
      ["GRID_OPERATOR", "Grid operator"],
    ],
  },
  {
    code: "GOVERNMENT_REGULATION",
    name: "Government and regulation",
    subcategories: [
      ["MINISTRY", "Ministry"],
      ["REGULATOR", "Regulator"],
      ["MUNICIPALITY", "Municipality"],
      ["TOURISM_AUTHORITY", "Tourism authority"],
      ["DEVELOPMENT_AGENCY", "Development agency"],
    ],
  },
  {
    code: "COMMUNITY_NONPROFIT",
    name: "Community and nonprofit",
    subcategories: [
      ["NONPROFIT", "Nonprofit"],
      ["FAITH_BASED_ORGANIZATION", "Faith-based organization"],
      ["COMMUNITY_ORGANIZATION", "Community organization"],
      ["FOUNDATION", "Foundation"],
    ],
  },
  {
    code: "PROFESSIONAL_SERVICES",
    name: "Professional services",
    subcategories: [
      ["LEGAL_SERVICES", "Legal services"],
      ["ACCOUNTING_SERVICES", "Accounting services"],
      ["ENGINEERING_SERVICES", "Engineering services"],
      ["TECHNOLOGY_SERVICES", "Technology services"],
      ["CONSULTING_SERVICES", "Consulting services"],
    ],
  },
] as const;

const SUBCATEGORY_TO_CATEGORY = new Map(
  ORGANIZATION_TAXONOMY.flatMap((category) =>
    category.subcategories.map(([code]) => [code, category.code] as const),
  ),
);

export const NodeRecordSchema = z.object({
  node_uid: z.string().regex(/^SE-IN-[A-Z]{2}-[A-Z0-9-]+-[0-9]{4}$/),
  program_code: z.literal("SIDEKICK-100D-2026"),
  plant_capacity_mw: z.number().positive().default(5),
  opportunity_zone_status: z.enum([
    "UNVERIFIED",
    "CANDIDATE",
    "VERIFIED",
    "NOT_ELIGIBLE",
    "EXPIRED",
  ]),
  stage: z.enum(NODE_STAGES),
  substation_name: z.string().trim().min(1).nullable().optional(),
  utility_owner: z.string().trim().min(1).nullable().optional(),
  community_name: z.string().trim().min(1).nullable().optional(),
  state_code: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .nullable()
    .optional(),
  county_name: z.string().trim().min(1).nullable().optional(),
  census_tract: z.string().trim().min(1).nullable().optional(),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  drive_evidence_folder_id: z.string().trim().min(1).nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const OrganizationIntakeSchema = z
  .object({
    legalName: z.string().trim().min(2).max(240),
    organizationType: z.enum([
      "GOVERNMENT",
      "UTILITY",
      "FINANCIAL",
      "ACADEMIC",
      "COMMUNITY",
      "VENDOR",
      "OTHER",
    ]),
    relationshipType: z
      .enum(ORG_RELATIONSHIP_TYPES)
      .default("EXTERNAL_PARTNER"),
    primaryCategoryCode: z.enum(ORG_CATEGORY_CODES),
    subcategoryCode: z.enum(ORG_SUBCATEGORY_CODES),
    jurisdiction: z.string().trim().min(2).max(160),
    registrationNumber: z.string().trim().max(120).nullable().optional(),
    website: z.string().trim().url().max(320).nullable().optional(),
    requestedRole: z.string().trim().min(2).max(160),
    representativeName: z.string().trim().min(2).max(160),
    representativeEmail: z.string().email().max(254),
    declaredRelationship: z.string().trim().min(4).max(1000),
    requestedCapabilities: z
      .array(z.enum(["VIEW", "CONTRIBUTE", "PROPOSE", "ADMINISTER", "EXECUTE"]))
      .min(1)
      .max(5),
    sourceCubeCode: z.string().trim().max(80).nullable().optional(),
    driveEvidenceFolderId: z.string().trim().max(240).nullable().optional(),
    idempotencyKey: z.string().uuid(),
  })
  .superRefine((value, context) => {
    if (
      SUBCATEGORY_TO_CATEGORY.get(value.subcategoryCode) !==
      value.primaryCategoryCode
    ) {
      context.addIssue({
        code: "custom",
        path: ["subcategoryCode"],
        message: "Select a subcategory within the chosen primary category.",
      });
    }
  });

export type NodeRecord = z.infer<typeof NodeRecordSchema>;
export type OrganizationIntake = z.infer<typeof OrganizationIntakeSchema>;
export type OrganizationIntegrationState =
  (typeof ORG_INTEGRATION_STATES)[number];
export type OrganizationRelationshipType =
  (typeof ORG_RELATIONSHIP_TYPES)[number];

export interface OrganizationQueueItem {
  id: string;
  legalName: string;
  organizationType: string;
  relationshipType: OrganizationRelationshipType;
  primaryCategoryCode: (typeof ORG_CATEGORY_CODES)[number];
  primaryCategoryName: string;
  subcategoryCode: (typeof ORG_SUBCATEGORY_CODES)[number];
  subcategoryName: string;
  jurisdiction: string;
  state: OrganizationIntegrationState;
  requestedRole: string;
  requestedCapabilities: string[];
  sourceCubeCode: string | null;
  checkCount: number;
  passedCheckCount: number;
  activeScopeCount: number;
  updatedAt: string;
}

export interface OrganizationQueuePage {
  items: OrganizationQueueItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DashboardReadout {
  generatedAt: string;
  program: { code: string; name: string; status: string };
  counts: {
    nodes: number;
    evidence: number;
    organizations: number;
    organizationsInReview: number;
    activeOrganizations: number;
    pendingDecisions: number;
  };
  checkpoints: Array<{
    code: string;
    day: number;
    title: string;
    dueAt: string;
    workStatus: string | null;
  }>;
  nodeStages: Array<{ stage: string; count: number; capacityMw: number }>;
  organizationQueue: OrganizationQueueItem[];
}

export * from "./workforce";
